import { useAuth } from "@clerk/clerk-react";
import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { AdminStripeCoupon } from "schemas/admin";

import Alert from "components/Alert";
import StyledPage from "components/Styled/Page";
import { fetcherAuth } from "utils/api";
import { useAlert } from "utils/useAlert";

import EditStripeCouponDrawer, {
  StripeCouponDrawerMode,
} from "./EditStripeCouponDrawer";

const formatTimestamp = (ts?: number | null) => {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleDateString();
};

const isInWindow = (coupon: AdminStripeCoupon): boolean => {
  if (!coupon.isActive) return false;
  const now = Date.now() / 1000;
  if (coupon.startDate && now < coupon.startDate) return false;
  if (coupon.endDate && now > coupon.endDate) return false;
  return true;
};

const AdminStripeCoupons = () => {
  const { getToken } = useAuth();
  const [message, severity, setAlert, closeAlert] = useAlert();

  const [loading, setLoading] = useState<boolean>(false);
  const [coupons, setCoupons] = useState<AdminStripeCoupon[]>([]);
  const [search, setSearch] = useState<string>("");
  const [drawerMode, setDrawerMode] = useState<StripeCouponDrawerMode | null>(
    null,
  );

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        "/api/admin/stripe-coupons",
        "GET",
      );
      setCoupons(res.stripeCoupons || []);
    } catch (error: any) {
      setAlert(error?.message || "Failed to load coupons", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const q = search.trim().toLowerCase();
  const filteredCoupons = q
    ? coupons.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          (c.description || "").toLowerCase().includes(q) ||
          (c.stripeCouponId || "").toLowerCase().includes(q) ||
          (c.stripePromoCodeId || "").toLowerCase().includes(q),
      )
    : coupons;

  const upsertCoupon = (saved: AdminStripeCoupon) => {
    setCoupons((prev) => {
      const exists = prev.some((c) => c.id === saved.id);
      if (exists) {
        return prev.map((c) => (c.id === saved.id ? saved : c));
      }
      return [saved, ...prev];
    });
  };

  const handleCopyCheckoutCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setAlert("Checkout code copied to clipboard", "success");
  };

  const handleSaved = (saved: AdminStripeCoupon) => {
    upsertCoupon(saved);
    setDrawerMode({ kind: "edit", coupon: saved });
    setAlert("Coupon saved", "success");
  };

  return (
    <StyledPage
      content={
        <>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loading}
          >
            <CircularProgress />
          </Backdrop>

          <Grid container sx={{ mt: 2 }} spacing={1} alignItems="center">
            <Grid item xs>
              <Typography variant="h3" gutterBottom>
                <strong>Stripe Coupons</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Schedule banner-promoted coupon sales. A coupon shows up on the
                banner and on its applicable plan rows whenever{" "}
                <strong>is_active</strong> is on and today's date is between the
                start and end dates.
              </Typography>
            </Grid>
            <Grid item xs="auto">
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={() => setDrawerMode({ kind: "create" })}
                >
                  New Coupon
                </Button>
                <Button variant="outlined" onClick={fetchCoupons}>
                  Refresh
                </Button>
              </Stack>
            </Grid>
          </Grid>

          <Grid container sx={{ my: 2 }} spacing={1}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Search by name, description, or Stripe ID"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Grid>
          </Grid>

          <TableContainer sx={{ maxHeight: "70vh" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Discount</TableCell>
                  <TableCell>Window</TableCell>
                  <TableCell>Applicable Plans</TableCell>
                  <TableCell>Showable Plans</TableCell>
                  <TableCell>Checkout Code</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCoupons.map((coupon) => (
                  <TableRow key={coupon.id} hover>
                    <TableCell>{coupon.id}</TableCell>
                    <TableCell>
                      <strong>{coupon.name}</strong>
                      {coupon.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", maxWidth: 280 }}
                        >
                          {coupon.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {coupon.discountPercentage != null
                        ? `${coupon.discountPercentage}%`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {formatTimestamp(coupon.startDate)} →{" "}
                      {formatTimestamp(coupon.endDate)}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      {coupon.applicablePlans &&
                      coupon.applicablePlans.length > 0 ? (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          {coupon.applicablePlans.map((plan) => (
                            <Chip key={plan} label={plan} size="small" />
                          ))}
                        </Stack>
                      ) : (
                        "All plans"
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      {coupon.showableToPlans &&
                      coupon.showableToPlans.length > 0 ? (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          {coupon.showableToPlans.map((plan) => (
                            <Chip key={plan} label={plan} size="small" />
                          ))}
                        </Stack>
                      ) : (
                        "Everyone"
                      )}
                    </TableCell>
                    <TableCell>
                      {coupon.checkoutCode ? (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {coupon.checkoutCode}
                          </Typography>
                          <Tooltip title="Copy checkout code">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleCopyCheckoutCode(coupon.checkoutCode!)
                              }
                            >
                              <Box
                                component="i"
                                className="fa-regular fa-clipboard"
                                sx={{ fontSize: 14 }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {isInWindow(coupon) ? (
                        <Chip label="live" color="success" size="small" />
                      ) : coupon.isActive ? (
                        <Chip label="scheduled / past" size="small" />
                      ) : (
                        <Chip label="inactive" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setDrawerMode({ kind: "edit", coupon })}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredCoupons.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 2, textAlign: "center" }}
                      >
                        No coupons found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <EditStripeCouponDrawer
            mode={drawerMode}
            onClose={() => setDrawerMode(null)}
            onSaved={handleSaved}
            setAlert={setAlert}
          />

          <Alert
            message={message}
            severity={severity}
            closeAlert={closeAlert}
          />
        </>
      }
    />
  );
};

export default AdminStripeCoupons;
